import {
  type CatalogStatus,
  type EscalationScope,
  type EffectiveOfferingSla,
  type OfferingSlaPolicy,
  type ServiceOfferingCatalogEntry,
  type ServiceOfferingSubcategoryCatalogEntry,
  type ServiceOfferingType,
  type ServiceSlaPolicy,
  type TierOfferingSlaOverride,
} from './serviceCatalogTypes';

export function parseNonNegativeIntOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.trunc(value));
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed)) return null;
    return Math.max(0, parsed);
  }
  return null;
}

export function parseBooleanOrNull(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return null;
}

export function parseEscalationScopeOrNull(value: unknown): EscalationScope | null {
  if (value === 'response' || value === 'resolution' || value === 'both') return value;
  return null;
}

export function toInputValue(value: number | null | undefined): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

export function normalizeCatalogStatus(value: unknown): CatalogStatus {
  return value === 'published' || value === 'archived' ? value : 'draft';
}

export function normalizeServiceOfferingType(value: unknown): ServiceOfferingType {
  if (value === 'advanced' || value === 'specialty') return value;
  return 'core';
}

export function normalizeOfferingSlaPolicy(value: unknown): OfferingSlaPolicy {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      responseTargetMinutes: null,
      resolutionTargetMinutes: null,
      targetCompletionMinutes: null,
      escalationAfterMinutes: null,
      escalationScope: null,
      queueWeight: null,
      businessHoursOnly: null,
    };
  }
  const raw = value as Record<string, unknown>;
  return {
    responseTargetMinutes: parseNonNegativeIntOrNull(raw.responseTargetMinutes),
    resolutionTargetMinutes: parseNonNegativeIntOrNull(raw.resolutionTargetMinutes),
    targetCompletionMinutes: parseNonNegativeIntOrNull(raw.targetCompletionMinutes),
    escalationAfterMinutes: parseNonNegativeIntOrNull(raw.escalationAfterMinutes),
    escalationScope: parseEscalationScopeOrNull(raw.escalationScope),
    queueWeight: parseNonNegativeIntOrNull(raw.queueWeight),
    businessHoursOnly: parseBooleanOrNull(raw.businessHoursOnly),
  };
}

export function normalizeServiceSlaPolicy(value: unknown): ServiceSlaPolicy {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      responseTargetMinutes: null,
      resolutionTargetMinutes: null,
      targetCompletionMinutes: null,
      escalationAfterMinutes: null,
      escalationScope: null,
      monthlyHoursCommitment: null,
      queueWeight: null,
      businessHoursOnly: false,
    };
  }
  const raw = value as Record<string, unknown>;
  return {
    responseTargetMinutes: parseNonNegativeIntOrNull(raw.responseTargetMinutes),
    resolutionTargetMinutes: parseNonNegativeIntOrNull(raw.resolutionTargetMinutes),
    targetCompletionMinutes: parseNonNegativeIntOrNull(raw.targetCompletionMinutes),
    escalationAfterMinutes: parseNonNegativeIntOrNull(raw.escalationAfterMinutes),
    escalationScope: parseEscalationScopeOrNull(raw.escalationScope),
    monthlyHoursCommitment: parseNonNegativeIntOrNull(raw.monthlyHoursCommitment),
    queueWeight: parseNonNegativeIntOrNull(raw.queueWeight),
    businessHoursOnly: Boolean(raw.businessHoursOnly),
  };
}

export function normalizeServiceOfferingCatalog(value: unknown): ServiceOfferingCatalogEntry[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const normalized: ServiceOfferingCatalogEntry[] = [];

  value.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;
    const raw = entry as Record<string, unknown>;
    const code = (typeof raw.code === 'string' ? raw.code : '').trim().toUpperCase();
    if (!code || seen.has(code)) return;
    seen.add(code);
    const name = (typeof raw.name === 'string' ? raw.name : '').trim();
    const sortOrderRaw = parseNonNegativeIntOrNull(raw.sortOrder);
    normalized.push({
      code,
      name: name || code,
      description: (typeof raw.description === 'string' ? raw.description : '').trim(),
      status: normalizeCatalogStatus(raw.status),
      sortOrder: sortOrderRaw ?? index,
      type: normalizeServiceOfferingType(raw.type),
      addOnEnabled: Boolean(raw.addOnEnabled),
      unitPriceCents: parseNonNegativeIntOrNull(raw.unitPriceCents),
      sla: normalizeOfferingSlaPolicy(raw.sla),
      subcategories: normalizeServiceOfferingSubcategories(raw.subcategories, code),
    });
  });

  return normalized.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

function normalizeServiceOfferingSubcategories(
  value: unknown,
  offeringCode: string,
): ServiceOfferingSubcategoryCatalogEntry[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const normalized: ServiceOfferingSubcategoryCatalogEntry[] = [];

  value.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;
    const raw = entry as Record<string, unknown>;
    const fallbackId = `${offeringCode}_SUBCATEGORY_${index + 1}`;
    const id = (typeof raw.id === 'string' ? raw.id : '').trim() || fallbackId;
    if (seen.has(id)) return;
    const name = (typeof raw.name === 'string' ? raw.name : '').trim();
    if (!name) return;
    seen.add(id);
    normalized.push({
      id,
      name,
      description: (typeof raw.description === 'string' ? raw.description : '').trim(),
      estimatedMinutes: parseNonNegativeIntOrNull(raw.estimatedMinutes),
      estimatedMinutesOverride: parseNonNegativeIntOrNull(raw.estimatedMinutesOverride),
      estimateSampleCount: parseNonNegativeIntOrNull(raw.estimateSampleCount) ?? 0,
      estimateUpdatedAt:
        typeof raw.estimateUpdatedAt === 'string' ? raw.estimateUpdatedAt.trim() || null : null,
      estimateSource:
        raw.estimateSource === 'seed' ||
        raw.estimateSource === 'aggregate' ||
        raw.estimateSource === 'none'
          ? raw.estimateSource
          : 'none',
    });
  });

  return normalized;
}

export function normalizeTierOfferingSlaOverrides(
  value: unknown,
  validOfferingCodes: Set<string>,
): Record<string, TierOfferingSlaOverride> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const raw = value as Record<string, unknown>;
  const next: Record<string, TierOfferingSlaOverride> = {};
  Object.entries(raw).forEach(([code, policy]) => {
    if (!validOfferingCodes.has(code)) return;
    if (!policy || typeof policy !== 'object' || Array.isArray(policy)) return;
    const normalized = normalizeOfferingSlaPolicy(policy);
    const hasAny =
      normalized.responseTargetMinutes !== null ||
      normalized.resolutionTargetMinutes !== null ||
      normalized.targetCompletionMinutes !== null ||
      normalized.escalationAfterMinutes !== null ||
      normalized.escalationScope !== null ||
      normalized.queueWeight !== null ||
      normalized.businessHoursOnly !== null;
    if (!hasAny) return;
    next[code] = normalized;
  });
  return next;
}

export function computeEffectiveOfferingSla(args: {
  serviceSla: ServiceSlaPolicy;
  offeringSla?: OfferingSlaPolicy | null;
  tierOverride?: TierOfferingSlaOverride | null;
}): EffectiveOfferingSla {
  const { serviceSla, offeringSla, tierOverride } = args;

  const pickNumber = (
    key:
      | 'responseTargetMinutes'
      | 'resolutionTargetMinutes'
      | 'targetCompletionMinutes'
      | 'escalationAfterMinutes'
      | 'queueWeight',
  ) => {
    const tierValue = tierOverride?.[key];
    if (typeof tierValue === 'number') return { value: tierValue, source: 'tier' as const };
    const offeringValue = offeringSla?.[key];
    if (typeof offeringValue === 'number') return { value: offeringValue, source: 'offering' as const };
    const serviceValue = serviceSla[key];
    if (typeof serviceValue === 'number') return { value: serviceValue, source: 'service' as const };
    return { value: null, source: 'none' as const };
  };

  const response = pickNumber('responseTargetMinutes');
  const resolution = pickNumber('resolutionTargetMinutes');
  const targetCompletion = pickNumber('targetCompletionMinutes');
  const escalation = pickNumber('escalationAfterMinutes');
  const queueWeight = pickNumber('queueWeight');
  const tierEscalationScope = tierOverride?.escalationScope;
  const offeringEscalationScope = offeringSla?.escalationScope;
  let escalationScope: EscalationScope | null = null;
  let escalationScopeSource: EffectiveOfferingSla['sources']['escalationScope'] = 'none';
  if (serviceSla.escalationScope) {
    escalationScope = serviceSla.escalationScope;
    escalationScopeSource = 'service';
  }
  if (offeringEscalationScope) {
    escalationScope = offeringEscalationScope;
    escalationScopeSource = 'offering';
  }
  if (tierEscalationScope) {
    escalationScope = tierEscalationScope;
    escalationScopeSource = 'tier';
  }

  const tierBusinessHours = tierOverride?.businessHoursOnly;
  const offeringBusinessHours = offeringSla?.businessHoursOnly;
  let businessHoursOnly = serviceSla.businessHoursOnly;
  let businessHoursSource: EffectiveOfferingSla['sources']['businessHoursOnly'] = 'service';
  if (typeof offeringBusinessHours === 'boolean') {
    businessHoursOnly = offeringBusinessHours;
    businessHoursSource = 'offering';
  }
  if (typeof tierBusinessHours === 'boolean') {
    businessHoursOnly = tierBusinessHours;
    businessHoursSource = 'tier';
  }

  return {
    responseTargetMinutes: response.value,
    resolutionTargetMinutes: resolution.value,
    targetCompletionMinutes: targetCompletion.value,
    escalationAfterMinutes: escalation.value,
    escalationScope,
    queueWeight: queueWeight.value,
    businessHoursOnly,
    sources: {
      responseTargetMinutes: response.source,
      resolutionTargetMinutes: resolution.source,
      targetCompletionMinutes: targetCompletion.source,
      escalationAfterMinutes: escalation.source,
      escalationScope: escalationScopeSource,
      queueWeight: queueWeight.source,
      businessHoursOnly: businessHoursSource,
    },
  };
}

export function hasMissingRequiredSlaTargets(sla: EffectiveOfferingSla): boolean {
  return sla.responseTargetMinutes === null || sla.resolutionTargetMinutes === null;
}

export function normalizeOfferingCode(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}
